import { Box, Button, Flex } from "@chakra-ui/react";
import { Comic } from "../../../types";
import { useHookstate } from "@hookstate/core";
import { useTranslation } from "react-i18next";
import { NavigationTrigger } from "../../../screens/reader/types";
import { useNavigateToReaderWithContext } from "../../../screens/reader/hooks/useNavigateToReaderWithContext";
import { useIsOverlayOpen } from "../../../screens/reader/hooks/hookstate/useIsOverlayOpen";
import { useReleaseData } from "../../../screens/reader/hooks/hookstate/useReleaseData";
import { rootComponentsStore } from "../../../hookstate/rootComponentsStore";
import { useIsInReader } from "../../../hooks/useIsInReader";
import { useMemo } from "react";
import { ComicDetailsMenu } from "./ComicDetailsMenu";

export interface ComicDetailsBottomBarProps {
  comicData: Comic;
}

export function ComicDetailsBottomBar({
  comicData,
}: ComicDetailsBottomBarProps) {
  const { toggleIsOverlayOpen } = useIsOverlayOpen();
  const inReader = useIsInReader();
  const { releaseData } = useReleaseData();
  const { t } = useTranslation();
  const slideInMenuConfig = useHookstate(rootComponentsStore.slideInMenuConfig);
  const navigationContext = slideInMenuConfig.navigationContext;

  const fullNavigationContext = useMemo(() => {
    return {
      ...navigationContext.get(),
      comicToNavigate: comicData,
      trigger: comicData.user_read_status
        ? NavigationTrigger.continueReadingButton
        : NavigationTrigger.startReadingButton,
    };
  }, [navigationContext, comicData]);

  const { goToCTAUrl } = useNavigateToReaderWithContext(fullNavigationContext);

  const navigate = releaseData?.comic_id !== comicData.id;
  return (
    <Flex
      height="96px"
      justifyContent="center"
      position="sticky"
      width="100%"
      bottom={0}
      flexDirection="row"
      backdropFilter="blur(18px)"
      background="#141517CC" //This is charcoal with 80% opacity
      boxShadow="0px 4px 24px 0px rgba(0, 0, 0, 0.40)"
      sx={{
        borderTopColor: "transparent.white.10",
      }}
      borderTop="1px solid"
      p="24px"
      gap="10px"
      marginTop="auto"
    >
      <Button
        id="btnContinueReading"
        variant="primary"
        size="lg"
        width="100%"
        aria-label="Continue reading"
        clipPath="inset(0 0 0 0 round 20px)"
        flex={1}
        textTransform="capitalize"
        onClick={!navigate ? toggleIsOverlayOpen : goToCTAUrl}
      >
        {(!inReader && comicData.user_read_status) ||
        !!comicData.user_read_status
          ? t("components.slideInMenu.comicDetailsMenu.continueReading")
          : t("components.slideInMenu.comicDetailsMenu.startReading")}
      </Button>
      <Box
        height="100%"
        width="52px"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <ComicDetailsMenu
          comic={comicData}
          backgroundColor="transparent.white.10"
          menuPlacement="top-end"
          iconSize="24px"
          buttonSize="52px"
          borderRadius="20px"
          gutter={16}
        />
      </Box>
    </Flex>
  );
}
